import ReactGA from 'react-ga4';
import posthog from 'posthog-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { config } from './config';
import { GAME_CONFIG } from './games';
import { rememberAuthReturnPath } from './utils/authReturnPath';
import {
  isAnalyticsConsentGranted,
} from './utils/consentPreferences';
import {
  safeLocalStorageGetItem,
  safeLocalStorageSetItem,
  safeLocalStorageRemoveItem,
} from './utils/safeStorage';

const isAdminPortal = process.env.REACT_APP_IS_ADMIN_PORTAL === 'true';
const APP_SURFACE = 'main_frontend';
const POSTHOG_DEFAULTS_VERSION = '2026-01-30';
const EVENT_VERSION = 1;

const escapeRegex = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const GAME_PATH_PATTERN = Object.values(GAME_CONFIG)
  .map((game) => escapeRegex(game.pathName))
  .join('|');
export const GAME_PLAY_PAGE_URL_REGEX = new RegExp(
  `/(?:${GAME_PATH_PATTERN})/\\d{4}-\\d{2}-\\d{2}(?:$|[/?#])`
);

const ANALYTICS_CLERK_USER_ID_KEY = 'analytics-clerk-user-id';
const PENDING_AUTH_CONTEXT_KEY = 'pending-auth-context-v1';
const PENDING_AUTH_CONTEXT_FRESHNESS_MS = 5 * 60 * 1000;
const POSTHOG_EVENT_DENYLIST = Array.isArray(config.POSTHOG_EVENT_DENYLIST) ? config.POSTHOG_EVENT_DENYLIST : [];
const GAME_GUESS_EVENT_REGEX = /^game_guess_/;
const firstSubmissionEventKeys = new Set();

let gaInitialized = false;
let gaEnabled = false;
let posthogInitialized = false;
let posthogEnabled = false;
let currentAnalyticsUserProperties = {};
let lastIdentifiedPostHogClerkUserId = null;
let lastSyncedPostHogUserProperties = {
  clerkUserId: null,
  properties: {},
};

const SENSITIVE_KEYS = new Set([
  'email',
  'username',
  'first_name',
  'last_name',
  'name',
  'message',
  'error',
  'stack',
  'token',
  'authorization',
  'password',
  'prompt',
  'input',
  'query',
  'text',
  'content',
]);

const SENSITIVE_KEY_PARTIALS = [
  'email',
  'token',
  'password',
  'auth',
  'cookie',
  'message',
  'stack',
  'prompt',
  'input',
  'query',
  'text',
  'content',
];

const NON_SENSITIVE_KEYS = new Set([
  'auth_mode',
  'error_code',
]);

const POSTHOG_RESERVED_PROPERTY_KEYS = new Set([
  'token',
  'distinct_id',
  '$anon_distinct_id',
  '$device_id',
  '$session_id',
  '$window_id',
  '$set',
  '$set_once',
]);

const SENSITIVE_URL_QUERY_PARAMS = new Set([
  '__clerk_handshake',
]);

const readPendingAuthContext = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(PENDING_AUTH_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

const savePendingAuthContext = (context = {}) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    const merged = {
      ...(readPendingAuthContext() || {}),
      ...context,
      updated_at: Date.now(),
    };
    window.sessionStorage.setItem(PENDING_AUTH_CONTEXT_KEY, JSON.stringify(merged));
  } catch (_) {}
};

const consumePendingAuthContext = () => {
  const context = readPendingAuthContext();
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(PENDING_AUTH_CONTEXT_KEY);
    }
  } catch (_) {}
  return context;
};

const hasFreshMatchingPendingAuthContext = (payload = {}) => {
  const pendingContext = readPendingAuthContext();
  if (!pendingContext) return false;

  const updatedAt = Number(pendingContext.updated_at) || 0;
  if (!updatedAt || (Date.now() - updatedAt) > PENDING_AUTH_CONTEXT_FRESHNESS_MS) {
    return false;
  }

  const matchesMode = normalizeNullable(pendingContext.auth_mode) === normalizeNullable(payload.cta_kind);
  const matchesSource = normalizeNullable(pendingContext.source_surface) === normalizeNullable(payload.source_surface);
  const matchesGateReason = normalizeNullable(pendingContext.gate_reason) === normalizeNullable(payload.gate_reason);
  const matchesNextPath = normalizeNullable(pendingContext.next_path) === normalizeNullable(payload.next_path);

  return matchesMode && matchesSource && matchesGateReason && matchesNextPath;
};

const getTrackedClerkUserId = () => safeLocalStorageGetItem(ANALYTICS_CLERK_USER_ID_KEY);
const setTrackedClerkUserId = (clerkUserId) => {
  if (!clerkUserId) return;
  safeLocalStorageSetItem(ANALYTICS_CLERK_USER_ID_KEY, String(clerkUserId));
};
const clearTrackedClerkUserId = () => safeLocalStorageRemoveItem(ANALYTICS_CLERK_USER_ID_KEY);

const isPostHogConfigured = () => {
  return Boolean(config.POSTHOG_ENABLED && config.POSTHOG_PROJECT_TOKEN && !isAdminPortal);
};

const normalizeNullable = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return value;
};

const buildCommonProperties = () => ({
  environment: config.ENVIRONMENT,
  app_surface: APP_SURFACE,
  client_git_sha: normalizeNullable(config.GIT_SHA),
});

const isAnalyticsDebugLoggingEnabled = () => config.ENVIRONMENT === 'development';

const debugAnalytics = (message, payload) => {
  if (!isAnalyticsDebugLoggingEnabled()) {
    return;
  }

  if (payload === undefined) {
    console.debug(`[Analytics] ${message}`);
    return;
  }

  console.debug(`[Analytics] ${message}`, sanitizeProperties(payload));
};

const withPosthogEventDefaults = (eventData = {}) => ({
  ...buildCommonProperties(),
  ...eventData,
});

const buildRequiredEventProperties = (overrides = {}) => {
  const identityUuid = normalizeNullable(overrides.identity_uuid) || getUserId();
  const clerkUserId = normalizeNullable(overrides.clerk_user_id) || normalizeNullable(getTrackedClerkUserId());
  const userState = overrides.user_state || (clerkUserId ? 'authenticated' : 'anonymous');

  return {
    event_version: EVENT_VERSION,
    ...buildCommonProperties(),
    user_state: userState,
    identity_uuid: identityUuid,
    clerk_user_id: clerkUserId,
    game_slug: normalizeNullable(overrides.game_slug),
    source_surface: normalizeNullable(overrides.source_surface),
    gate_reason: normalizeNullable(overrides.gate_reason),
    ...overrides,
  };
};

const isSensitiveKey = (key) => {
  const normalized = String(key || '').toLowerCase();
  if (POSTHOG_RESERVED_PROPERTY_KEYS.has(normalized) || normalized.startsWith('$')) {
    return false;
  }
  if (NON_SENSITIVE_KEYS.has(normalized)) {
    return false;
  }
  if (SENSITIVE_KEYS.has(normalized)) return true;
  return SENSITIVE_KEY_PARTIALS.some((part) => normalized.includes(part));
};

const redactSensitiveQueryParamsInUrlLikeValue = (value) => {
  if (typeof value !== 'string' || !value.includes('?')) {
    return value;
  }

  const hashIndex = value.indexOf('#');
  const beforeHash = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const hashFragment = hashIndex >= 0 ? value.slice(hashIndex) : '';
  const queryStartIndex = beforeHash.indexOf('?');

  if (queryStartIndex < 0) {
    return value;
  }

  const base = beforeHash.slice(0, queryStartIndex);
  const queryString = beforeHash.slice(queryStartIndex + 1);
  const params = new URLSearchParams(queryString);
  let changed = false;

  for (const queryParam of SENSITIVE_URL_QUERY_PARAMS) {
    if (params.has(queryParam)) {
      params.set(queryParam, 'REDACTED');
      changed = true;
    }
  }

  if (!changed) {
    return value;
  }

  const safeQuery = params.toString();

  if (!base) {
    const safeSearch = safeQuery ? `?${safeQuery}` : '';
    return `${safeSearch}${hashFragment}`;
  }

  const safeSearch = safeQuery ? `?${safeQuery}` : '';
  return `${base}${safeSearch}${hashFragment}`;
};

const sanitizeValue = (key, value) => {
  if (value === null || value === undefined) return value;

  if (isSensitiveKey(key)) {
    return '[REDACTED]';
  }

  if (typeof value === 'string') {
    return redactSensitiveQueryParamsInUrlLikeValue(value);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeValue(key, entry));
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([nestedKey, nestedValue]) => [nestedKey, sanitizeValue(nestedKey, nestedValue)])
    );
  }

  return value;
};

const sanitizeProperties = (properties = {}) => {
  if (!properties || typeof properties !== 'object') {
    return {};
  }

  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [key, sanitizeValue(key, value)])
  );
};

const normalizeUserProperties = (properties = {}) => {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(properties)
      .filter(([key]) => Boolean(key))
      .map(([key, value]) => [key, value === undefined ? null : value])
  );
};

const areAnalyticsValuesEqual = (left, right) => {
  return JSON.stringify(left) === JSON.stringify(right);
};

const diffProperties = (nextProperties = {}, previousProperties = {}) => {
  return Object.fromEntries(
    Object.entries(nextProperties).filter(([key, value]) => !areAnalyticsValuesEqual(value, previousProperties[key]))
  );
};

const mergeAnalyticsUserProperties = (properties = {}) => {
  const normalizedProperties = normalizeUserProperties(properties);
  const nextProperties = {
    ...currentAnalyticsUserProperties,
    ...normalizedProperties,
  };
  const changedProperties = diffProperties(nextProperties, currentAnalyticsUserProperties);
  currentAnalyticsUserProperties = nextProperties;
  return {
    nextProperties,
    changedProperties,
  };
};

const buildRegisteredIdentityProperties = (clerkUserId) => {
  const anonymousUserId = getUserId();
  return sanitizeProperties(
    buildRequiredEventProperties({
      identity_uuid: anonymousUserId,
      clerk_user_id: clerkUserId,
      user_state: 'authenticated',
      account_type: 'registered',
    })
  );
};

const syncPostHogUserPropertiesIfNeeded = (clerkUserId, properties = {}) => {
  if (!clerkUserId || !posthogInitialized || !posthogEnabled) {
    return;
  }

  const sanitizedProperties = sanitizeProperties(normalizeUserProperties(properties));
  const previousProperties = lastSyncedPostHogUserProperties.clerkUserId === clerkUserId
    ? lastSyncedPostHogUserProperties.properties
    : {};
  const changedProperties = diffProperties(sanitizedProperties, previousProperties);

  if (Object.keys(changedProperties).length === 0) {
    return;
  }

  try {
    debugAnalytics('Calling PostHog $set', {
      clerk_user_id: clerkUserId,
      $set: changedProperties,
    });
    posthog.capture('$set', {
      $set: changedProperties,
    });
    lastSyncedPostHogUserProperties = {
      clerkUserId,
      properties: sanitizedProperties,
    };
  } catch (error) {
    console.warn('[Analytics] Failed to sync PostHog user properties:', error);
  }
};

const buildPatternRegex = (pattern = '') => {
  const escaped = escapeRegex(String(pattern || ''));
  return new RegExp(`^${escaped.replace(/\\\*/g, '.*')}$`);
};

const POSTHOG_DENYLIST_REGEXES = POSTHOG_EVENT_DENYLIST.map(buildPatternRegex);

const matchesAnyPattern = (eventName, regexes) => {
  return regexes.some((regex) => regex.test(eventName));
};

const shouldCapturePostHogEvent = (eventName) => {
  if (POSTHOG_DENYLIST_REGEXES.length > 0 && matchesAnyPattern(eventName, POSTHOG_DENYLIST_REGEXES)) {
    return { shouldCapture: false, reason: 'denylist' };
  }
  return { shouldCapture: true, reason: null };
};

const buildFirstSubmissionKey = (eventData = {}) => {
  const gameSlug = normalizeNullable(eventData.game_slug);
  const gameDate = normalizeNullable(eventData.game_date);
  if (!gameSlug || !gameDate) {
    return null;
  }
  const userKey = normalizeNullable(getTrackedClerkUserId()) || getUserId();
  return `${userKey}:${gameSlug}:${gameDate}`;
};

const resolvePostHogCaptureCandidates = (eventName, eventData = {}) => {
  if (GAME_GUESS_EVENT_REGEX.test(eventName)) {
    const firstSubmissionKey = buildFirstSubmissionKey(eventData);
    if (firstSubmissionKey && !firstSubmissionEventKeys.has(firstSubmissionKey)) {
      firstSubmissionEventKeys.add(firstSubmissionKey);
      return [{
        eventName: 'game_first_submission',
        eventData: {
          ...eventData,
          source_event: eventName,
        },
      }];
    }
    return [];
  }

  return [{ eventName, eventData }];
};

const capturePostHogEvent = (eventName, eventData = {}) => {
  if (!posthogEnabled) return;

  try {
    const candidates = resolvePostHogCaptureCandidates(eventName, eventData);
    for (const candidate of candidates) {
      const decision = shouldCapturePostHogEvent(candidate.eventName);
      if (!decision.shouldCapture) {
        console.info(
          `[Analytics] Dropped PostHog event "${candidate.eventName}" by gateway filter (${decision.reason})`
        );
        continue;
      }
      posthog.capture(
        candidate.eventName,
        sanitizeProperties(withPosthogEventDefaults(candidate.eventData))
      );
    }
  } catch (error) {
    console.warn('[Analytics] Failed to log PostHog event:', error);
  }
};

const ensureGaInitialized = () => {
  const measurementId = config.GA_MEASUREMENT_ID;
  if (!measurementId) {
    gaEnabled = false;
    return false;
  }

  if (!gaInitialized) {
    ReactGA.initialize(measurementId, {
      debug: config.ENVIRONMENT === 'development',
      gaOptions: {
        siteSpeedSampleRate: 100,
      },
    });
    gaInitialized = true;
  }

  gaEnabled = true;
  return true;
};

const buildPostHogInitOptions = ({ distinctId = null, isIdentified = false } = {}) => {
  const bootstrapDistinctId = normalizeNullable(distinctId);
  const bootstrapConfig = bootstrapDistinctId
    ? {
      distinctID: bootstrapDistinctId,
      isIdentifiedID: Boolean(isIdentified),
    }
    : undefined;

  return {
    api_host: config.POSTHOG_HOST || 'https://us.i.posthog.com',
    ui_host: 'https://us.posthog.com',
    defaults: POSTHOG_DEFAULTS_VERSION,
    person_profiles: 'identified_only',
    bootstrap: bootstrapConfig,
    capture_pageview: 'history_change',
    capture_pageleave: 'if_capture_pageview',
    opt_out_capturing_by_default: true,
    session_recording: {
      full_snapshot_interval_millis: 120000,
    },
    autocapture: false,
    before_send: (event) => {
      if (!event || !event.properties) {
        return event;
      }
      return {
        ...event,
        properties: sanitizeProperties(event.properties),
      };
    },
    loaded: (instance) => {
      instance.register(buildCommonProperties());
    },
  };
};

const ensurePostHogInitialized = (distinctId = null) => {
  if (!isPostHogConfigured()) {
    posthogEnabled = false;
    return false;
  }

  if (!posthogInitialized) {
    posthog.init(
      config.POSTHOG_PROJECT_TOKEN,
      buildPostHogInitOptions({ distinctId, isIdentified: false })
    );
    posthogInitialized = true;
  }

  posthogEnabled = true;
  return true;
};

const updateGaUserId = (userId) => {
  if (!gaEnabled || !userId) return;

  try {
    ReactGA.set({ user_id: userId });
    if (config.GA_MEASUREMENT_ID) {
      ReactGA.gtag('config', config.GA_MEASUREMENT_ID, { user_id: userId });
    }
  } catch (error) {
    console.warn('[Analytics] Failed to set GA user ID:', error);
  }
};

const updatePostHogAnonymousIdentity = () => {
  if (!posthogInitialized || !posthogEnabled) return;

  const userId = getUserId();
  try {
    lastIdentifiedPostHogClerkUserId = null;
    posthog.reset();
    posthog.init(
      config.POSTHOG_PROJECT_TOKEN,
      buildPostHogInitOptions({ distinctId: userId, isIdentified: false })
    );
    posthog.opt_in_capturing();
    posthog.register(
      sanitizeProperties(
        buildRequiredEventProperties({
          identity_uuid: userId,
          clerk_user_id: null,
          user_state: 'anonymous',
          account_type: 'anonymous',
        })
      )
    );
    posthog.startSessionRecording?.();
  } catch (error) {
    console.warn('[Analytics] Failed to reset PostHog anonymous identity:', error);
  }
};

// Generate a UUID v4
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Get or create user ID
export const getUserId = () => {
  let userId = safeLocalStorageGetItem('user-id');

  if (!userId) {
    userId = generateUUID();
    safeLocalStorageSetItem('user-id', userId);
    console.log('[Analytics] Generated new user ID:', userId);
  }

  return userId;
};

// Always check localStorage for analytics consent
export const isAnalyticsAllowed = () => {
  if (isAnalyticsConsentGranted()) {
    return true;
  }

  const legacyConsent = safeLocalStorageGetItem('analytics-consent');
  return legacyConsent === 'true';
};

const ANALYTICS_COOKIE_PREFIXES = [
  '_ga',
  '_gid',
  '_gat',
  '_gcl_au',
  '_hj',
  'ph_',
];

const shouldDeleteCookie = (cookieName = '') => {
  return ANALYTICS_COOKIE_PREFIXES.some((prefix) => cookieName.startsWith(prefix));
};

const buildCandidateDomains = () => {
  if (typeof window === 'undefined') return [''];
  const hostname = window.location.hostname || '';
  if (!hostname) return [''];

  const parts = hostname.split('.').filter(Boolean);
  const domains = [''];
  for (let i = 0; i < parts.length; i += 1) {
    const domain = parts.slice(i).join('.');
    domains.push(domain);
    domains.push(`.${domain}`);
  }
  return Array.from(new Set(domains));
};

const expireCookie = (name, domain, path) => {
  const domainPart = domain ? `; domain=${domain}` : '';
  const pathPart = path ? `; path=${path}` : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}; SameSite=Lax`;
};

export const clearAnalyticsCookies = () => {
  if (typeof document === 'undefined') return;

  const cookiePairs = (document.cookie || '').split(';');
  const cookieNames = cookiePairs
    .map((entry) => entry.trim().split('=')[0])
    .filter(Boolean)
    .filter(shouldDeleteCookie);

  if (cookieNames.length === 0) return;

  const candidateDomains = buildCandidateDomains();
  for (const cookieName of cookieNames) {
    expireCookie(cookieName, '', '/');
    expireCookie(cookieName, '', '');
    for (const domain of candidateDomains) {
      expireCookie(cookieName, domain, '/');
      expireCookie(cookieName, domain, '');
    }
  }
};

// Initialize analytics providers
export const initGA = () => {
  if (isAdminPortal) {
    disableGA();
    return;
  }

  if (!isAnalyticsAllowed()) {
    disableGA();
    console.log('[Analytics] Not initializing analytics: consent not granted.');
    return;
  }

  const gaReady = ensureGaInitialized();
  const posthogReady = ensurePostHogInitialized(getUserId());

  const userId = getUserId();

  if (gaReady) {
    updateGaUserId(userId);
  }

  if (posthogReady) {
    try {
      posthog.opt_in_capturing();
      posthog.register(
        sanitizeProperties(
          buildRequiredEventProperties({
            identity_uuid: userId,
            clerk_user_id: null,
            user_state: 'anonymous',
            account_type: 'anonymous',
          })
        )
      );
      posthog.startSessionRecording?.();
      posthogEnabled = true;

      const trackedClerkUserId = normalizeNullable(getTrackedClerkUserId());
      if (trackedClerkUserId) {
        identifySignedInUser(trackedClerkUserId, currentAnalyticsUserProperties);
      }
    } catch (error) {
      console.warn('[Analytics] Failed to initialize PostHog capture:', error);
    }
  }
};

// Disable analytics collection
export const disableGA = () => {
  gaEnabled = false;

  if (posthogInitialized) {
    try {
      posthog.stopSessionRecording?.();
      posthog.opt_out_capturing();
    } catch (error) {
      console.warn('[Analytics] Failed to disable PostHog capture:', error);
    }
  }

  posthogEnabled = false;
  clearAnalyticsCookies();
};

// Track page views
const buildPageViewEventData = (path, title = document.title) => {
  return {
    hitType: 'pageview',
    page: path,
    title,
  };
};

export const logGaPageView = (path, title = document.title) => {
  const eventData = buildPageViewEventData(path, title);

  if (gaEnabled) {
    try {
      ReactGA.send(eventData);
    } catch (error) {
      console.warn('[Analytics] Failed to log GA page view:', error);
    }
  }
};

export const logPageView = (path, title = document.title) => {
  logGaPageView(path, title);

  capturePostHogEvent('$pageview', {
    path,
    title,
    $current_url: window.location?.href || path,
  });
};

// Track custom events
export const logEvent = (eventName, eventData = {}) => {
  if (gaEnabled) {
    try {
      ReactGA.event(eventName, eventData);
    } catch (error) {
      console.warn('[Analytics] Failed to log GA event:', error);
    }
  }

  capturePostHogEvent(eventName, eventData);
};

export const trackGuestRewardRedemption = ({ status, redeemed_claim_count = 0, gettoncini_delta = 0, stelline_delta = 0, learned_terms_delta = 0 }) => {
  logEvent('guest_reward_redemption_result', { status, redeemed_claim_count, gettoncini_delta, stelline_delta, learned_terms_delta });
};

export const getFeatureFlagVariant = (flagKey) => {
  if (!flagKey || !posthogInitialized || !posthogEnabled) {
    return null;
  }

  try {
    if (typeof posthog.getFeatureFlag === 'function') {
      return normalizeNullable(posthog.getFeatureFlag(flagKey));
    }
  } catch (error) {
    console.warn('[Analytics] Failed to read PostHog feature flag:', error);
  }

  return null;
};

export const useFeatureFlagVariant = (flagKey, fallbackValue = null) => {
  const [variant, setVariant] = useState(() => getFeatureFlagVariant(flagKey) ?? fallbackValue);

  useEffect(() => {
    if (!flagKey) {
      setVariant(fallbackValue);
      return undefined;
    }

    const syncVariant = () => {
      const nextVariant = getFeatureFlagVariant(flagKey);
      setVariant(nextVariant ?? fallbackValue);
    };

    syncVariant();

    if (typeof posthog.onFeatureFlags === 'function') {
      posthog.onFeatureFlags(syncVariant);
      return () => {
        if (typeof posthog.offFeatureFlags === 'function') {
          posthog.offFeatureFlags(syncVariant);
        }
      };
    }

    return undefined;
  }, [fallbackValue, flagKey]);

  return variant;
};

// Track homepage events with custom details
export const logHomepageEvent = (eventName, eventData) => {
  const namespacedEventName = `homepage_${eventName}`;
  logEvent(namespacedEventName, eventData);
};

// Track game events
export const logGameEvent = (eventName, gameSlug, gameDate, params) => {
  const namespacedEventName = `game_${eventName}`;
  const eventData = {
    game_slug: gameSlug,
    game_date: gameDate,
    ...params,
  };
  logEvent(namespacedEventName, eventData);
};

export const trackGameHintInsufficientBalance = ({
  gameSlug,
  gameDate,
  hintType,
  source = 'client_precheck',
  balanceBefore = null,
  hintCost = null,
  modalName = 'aiutini',
} = {}) => {
  const normalizedSource = source === 'server_response' ? 'server_response' : 'client_precheck';

  logGameEvent('hint_insufficient_balance', gameSlug, gameDate, {
    hint_type: hintType || null,
    source: normalizedSource,
    balance_before: typeof balanceBefore === 'number' ? balanceBefore : null,
    hint_cost: typeof hintCost === 'number' ? hintCost : null,
    modal_name: modalName || 'aiutini',
  });
};

const withAuthEventDefaults = (payload = {}, forced = {}) => {
  return buildRequiredEventProperties({
    source_surface: payload.source_surface || null,
    gate_reason: payload.gate_reason || null,
    game_slug: payload.game_slug || null,
    ...payload,
    ...forced,
  });
};

const withUiEventDefaults = (payload = {}, forced = {}) => {
  return buildRequiredEventProperties({
    source_surface: payload.source_surface || payload.surface || null,
    gate_reason: payload.gate_reason || null,
    game_slug: payload.game_slug || null,
    ...payload,
    ...forced,
  });
};

const toSafeInternalPath = (value, fallback = '/') => {
  const normalized = normalizeNullable(value);
  if (typeof normalized !== 'string') {
    return fallback;
  }
  const trimmed = normalized.trim();
  if (!trimmed || !trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return fallback;
  }
  return trimmed;
};

const UI_MODAL_NAME_ALIASES = {
  aiuto: 'aiutini',
  aiutini: 'aiutini',
  answers: 'solutions',
  benvenuti: 'welcome',
  benvenuto: 'welcome',
  contatore_di_parole: 'word_counter',
  contatore_parole: 'word_counter',
  generic: 'generic',
  gettoncini: 'gettoncini',
  gettoncini_info: 'gettoncini',
  impostazioni_utente: 'user_settings',
  install_helper: 'install_helper',
  installa_lapp: 'install_helper',
  instructions: 'instructions',
  istruzioni: 'instructions',
  la_tua_serie: 'streak',
  learned_terms_info: 'word_counter',
  newsletter_signup: 'newsletter_signup',
  nuova_versione_disponibile: 'replay_confirmation',
  replay_confirmation: 'replay_confirmation',
  soluzioni: 'solutions',
  streak_info: 'streak',
  untitled_modal: 'generic',
  user_settings: 'user_settings',
  word_counter: 'word_counter',
};

const UI_CLICK_NAME_ALIASES = {
  account_entry: 'account',
  accedi: 'login',
  aiuto: 'aiutini',
  aiutini: 'aiutini',
  answers: 'solutions',
  auth_sign_in: 'login',
  auth_sign_up: 'register',
  benvenuti: 'welcome',
  benvenuto: 'welcome',
  contatore_di_parole: 'word_counter',
  contatore_parole: 'word_counter',
  gettoncini: 'gettoncini',
  gettoncini_display: 'gettoncini',
  impostazioni_utente: 'user_settings',
  install_helper: 'install_helper',
  installa_lapp: 'install_helper',
  instructions: 'instructions',
  istruzioni: 'instructions',
  la_tua_serie: 'streak',
  learned_terms_display: 'word_counter',
  profile_entry: 'account',
  registrati: 'register',
  sign_in_cta: 'login',
  sign_up_cta: 'register',
  soluzioni: 'solutions',
  streak_display: 'streak',
  user_settings: 'user_settings',
  word_counter: 'word_counter',
};

const UI_CLICK_EVENT_NAME_BY_ELEMENT = {
  account: 'account_clicked',
  gettoncini: 'gettoncini_clicked',
  login: 'login_clicked',
  register: 'register_clicked',
  streak: 'streak_clicked',
  word_counter: 'word_counter_clicked',
};

const toAnalyticsToken = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  return raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_');
};

const toCanonicalUiName = (value, aliases, fallback = 'unknown') => {
  const token = toAnalyticsToken(value);
  if (!token) return fallback;
  return aliases[token] || token;
};

const toCanonicalUiClickName = (value, fallback = 'unknown') => {
  return toCanonicalUiName(value, UI_CLICK_NAME_ALIASES, fallback);
};

const toCanonicalUiModalName = (value, fallback = 'generic') => {
  return toCanonicalUiName(value, UI_MODAL_NAME_ALIASES, fallback);
};

const getUiElementName = (payload = {}) => {
  return toCanonicalUiClickName(payload.element_id || payload.element_name || payload.element_title, 'unknown_element');
};

const getUiModalName = (payload = {}) => {
  return toCanonicalUiModalName(payload.modal_name || payload.title, 'generic');
};

const resolveUiClickEventName = (payload = {}) => {
  const sourceSurface = payload.source_surface || payload.surface || null;
  const action = payload.action || null;
  const elementName = getUiElementName(payload);
  const isGameHeader = sourceSurface === 'game_header' || payload.surface === 'game_header_actions';
  if (isGameHeader) {
    return `game_click_${elementName}`;
  }

  const isDrawerToggle = sourceSurface === 'navbar' && elementName === 'hamburger_menu';
  const isDrawerClose = sourceSurface === 'drawer_menu' && elementName === 'drawer_close';
  if ((isDrawerToggle || isDrawerClose) && action === 'open') {
    return 'drawer_menu_opened';
  }
  if ((isDrawerToggle || isDrawerClose) && action === 'close') {
    return 'drawer_menu_closed';
  }

  const explicitIntentEvent = UI_CLICK_EVENT_NAME_BY_ELEMENT[elementName];
  if (explicitIntentEvent) {
    return explicitIntentEvent;
  }

  const isDrawerMenuNavigationClick = sourceSurface === 'drawer_menu' && action === 'click';
  if (isDrawerMenuNavigationClick) {
    return `drawer_menu_${elementName}_clicked`;
  }

  return `${elementName}_clicked`;
};

export const trackAuthGateViewed = (payload = {}) => {
  logEvent('auth_gate_viewed', withAuthEventDefaults(payload));
};

export const trackAuthCtaClicked = (payload = {}) => {
  savePendingAuthContext({
    auth_mode: payload.cta_kind === 'sign_up' ? 'sign_up' : payload.cta_kind === 'sign_in' ? 'sign_in' : null,
    source_surface: payload.source_surface || null,
    gate_reason: payload.gate_reason || null,
    next_path: payload.next_path || null,
    is_returning_device: payload.is_returning_device ?? null,
  });
  logEvent('auth_cta_clicked', withAuthEventDefaults(payload));
};

export const maybeTrackAuthCtaFromAuthPage = (payload = {}) => {
  if (!payload.source_surface || !payload.gate_reason || !payload.cta_kind) {
    return false;
  }

  if (hasFreshMatchingPendingAuthContext(payload)) {
    return false;
  }

  trackAuthCtaClicked({
    ...payload,
    trigger: payload.trigger || 'auth_page_fallback',
  });
  return true;
};

export const trackAuthPageViewed = (payload = {}) => {
  savePendingAuthContext({
    auth_mode: payload.auth_mode || null,
    source_surface: payload.source_surface || null,
    gate_reason: payload.gate_reason || null,
    next_path: payload.next_path || null,
  });
  logEvent('auth_page_viewed', withAuthEventDefaults(payload));
};

export const trackAuthCompleted = (payload = {}) => {
  const pendingContext = consumePendingAuthContext() || {};
  logEvent(
    'auth_completed',
    withAuthEventDefaults(
      {
        ...pendingContext,
        ...payload,
      },
      { user_state: 'authenticated' }
    )
  );
};

export const trackAuthPromptDismissed = (payload = {}) => {
  logEvent('auth_prompt_dismissed', withAuthEventDefaults(payload));
};

// Build link destinations without recording a click or changing return-path storage.
export const buildAuthRedirectUrl = (payload = {}) => {
  const ctaKind = payload.cta_kind === 'sign_in' ? 'sign_in' : 'sign_up';
  const authPath = ctaKind === 'sign_in' ? '/signin' : '/signup';
  const nextPath = toSafeInternalPath(payload.next_path, '/');
  const sourceSurface = normalizeNullable(payload.source_surface);
  const gateReason = normalizeNullable(payload.gate_reason);
  const message = normalizeNullable(payload.message);
  const params = new URLSearchParams();
  if (message) {
    params.set('message', message);
  }
  if (nextPath && nextPath !== '/') {
    params.set('next', nextPath);
    params.set('redirect_url', nextPath);
  }
  if (sourceSurface) {
    params.set('source', sourceSurface);
  }
  if (gateReason) {
    params.set('gate_reason', gateReason);
  }

  const queryString = params.toString();
  return queryString ? `${authPath}?${queryString}` : authPath;
};

export const buildTrackedAuthRedirectUrl = (payload = {}) => {
  const ctaKind = payload.cta_kind === 'sign_in' ? 'sign_in' : 'sign_up';
  const nextPath = toSafeInternalPath(payload.next_path, '/');
  const sourceSurface = normalizeNullable(payload.source_surface);
  const gateReason = normalizeNullable(payload.gate_reason);
  const isReturningDevice = payload.is_returning_device ?? null;
  const gameSlug = normalizeNullable(payload.game_slug);

  if (payload.track_gate_viewed) {
    trackAuthGateViewed({
      source_surface: sourceSurface,
      gate_reason: gateReason,
      next_path: nextPath,
      is_returning_device: isReturningDevice,
      game_slug: gameSlug,
      trigger: payload.gate_trigger || payload.trigger || null,
    });
  }

  trackAuthCtaClicked({
    source_surface: sourceSurface,
    gate_reason: gateReason,
    cta_kind: ctaKind,
    next_path: nextPath,
    is_returning_device: isReturningDevice,
    game_slug: gameSlug,
    trigger: payload.trigger || null,
  });

  // Persist before navigation so an immediate Clerk step/reload cannot race it.
  rememberAuthReturnPath(nextPath);
  return buildAuthRedirectUrl(payload);
};

export const trackAiHelperPanelOpened = (payload = {}) => {
  logEvent('ai_helper_panel_opened', withAuthEventDefaults(payload));
};

export const trackAiHelperAuthRedirected = (payload = {}) => {
  logEvent('ai_helper_auth_redirected', withAuthEventDefaults(payload));
};

export const trackAiHelperRequestClicked = (payload = {}) => {
  logEvent('ai_helper_request_clicked', withAuthEventDefaults(payload));
};

export const trackUiElementClicked = (payload = {}) => {
  const elementName = getUiElementName(payload);
  const eventName = resolveUiClickEventName(payload);

  logEvent(
    eventName,
    withUiEventDefaults({
      ...payload,
      element_name: elementName,
    })
  );
};

export const trackUiModalOpened = (payload = {}) => {
  const modalName = getUiModalName(payload);
  logEvent(
    `ui_modal_${modalName}_opened`,
    withUiEventDefaults({
      ...payload,
      modal_name: modalName,
    })
  );
};

export const trackUiModalClosed = (payload = {}) => {
  const modalName = getUiModalName(payload);
  logEvent(
    `ui_modal_${modalName}_closed`,
    withUiEventDefaults({
      ...payload,
      modal_name: modalName,
    })
  );
};

export const trackInterstitialAdViewed = (payload = {}) => {
  logEvent(
    'interstitial_ad_viewed',
    buildRequiredEventProperties({
      source_surface: payload.source_surface || 'ads_interstitial',
      game_slug: payload.game_slug || null,
      game_date: payload.game_date || null,
      placement_name: payload.placement_name || null,
      provider_type: payload.provider_type || null,
      exit_type: payload.exit_type || null,
      target_path: payload.target_path || null,
      source_stage: payload.source_stage || null,
      ad_unit_path: payload.ad_unit_path || null,
      slot_element_id: payload.slot_element_id || null,
      response_identifier: payload.response_identifier || null,
      in_view_percentage: typeof payload.in_view_percentage === 'number'
        ? payload.in_view_percentage
        : null,
      creative_id: payload.creative_id ?? null,
      line_item_id: payload.line_item_id ?? null,
      source_agnostic_creative_id: payload.source_agnostic_creative_id ?? null,
      source_agnostic_line_item_id: payload.source_agnostic_line_item_id ?? null,
    })
  );
};

export const trackRewardedAdCtaClicked = (payload = {}) => {
  logEvent(
    'rewarded_ad_cta_clicked',
    buildRequiredEventProperties({
      source_surface: payload.source_surface || null,
      game_slug: payload.game_slug || null,
      placement_name: payload.placement_name || null,
      reward_amount: typeof payload.reward_amount === 'number' ? payload.reward_amount : null,
    })
  );
};

export const trackRewardedAdStarted = (payload = {}) => {
  logEvent(
    'rewarded_ad_started',
    buildRequiredEventProperties({
      source_surface: payload.source_surface || null,
      game_slug: payload.game_slug || null,
      placement_name: payload.placement_name || null,
      provider_type: payload.provider_type || null,
    })
  );
};

export const trackRewardedAdClaimSucceeded = (payload = {}) => {
  logEvent(
    'rewarded_ad_claim_succeeded',
    buildRequiredEventProperties({
      source_surface: payload.source_surface || null,
      game_slug: payload.game_slug || null,
      placement_name: payload.placement_name || null,
      reward_amount: typeof payload.reward_amount === 'number' ? payload.reward_amount : null,
    })
  );
};

export const trackRewardedAdClaimFailed = (payload = {}) => {
  logEvent(
    'rewarded_ad_claim_failed',
    buildRequiredEventProperties({
      source_surface: payload.source_surface || null,
      game_slug: payload.game_slug || null,
      placement_name: payload.placement_name || null,
      error_code: payload.error_code || null,
    })
  );
};

export const identifySignedInUser = (clerkUserId, properties = {}) => {
  if (!clerkUserId) return;

  const {
    nextProperties,
    changedProperties,
  } = mergeAnalyticsUserProperties({
    account_type: 'registered',
    ...properties,
  });

  setTrackedClerkUserId(clerkUserId);

  if (gaEnabled) {
    updateGaUserId(clerkUserId);
  }

  if (!posthogInitialized || !posthogEnabled) return;

  try {
    const identityProps = buildRegisteredIdentityProperties(clerkUserId);
    const personProperties = {
      ...identityProps,
      ...sanitizeProperties(nextProperties),
    };
    const currentDistinctId = typeof posthog.get_distinct_id === 'function'
      ? normalizeNullable(posthog.get_distinct_id())
      : null;
    if (currentDistinctId === clerkUserId) {
      lastIdentifiedPostHogClerkUserId = clerkUserId;
    }

    const shouldIdentify = currentDistinctId !== clerkUserId
      && lastIdentifiedPostHogClerkUserId !== clerkUserId;
    const hasUserPropertyChanges = Object.keys(changedProperties).length > 0;

    if (shouldIdentify) {
      debugAnalytics('Calling PostHog identify', {
        distinct_id: clerkUserId,
        person_properties: personProperties,
      });
      posthog.identify(clerkUserId);
      lastIdentifiedPostHogClerkUserId = clerkUserId;
    }

    if (shouldIdentify || hasUserPropertyChanges) {
      posthog.register({
        ...personProperties,
      });
    }

    syncPostHogUserPropertiesIfNeeded(clerkUserId, personProperties);
  } catch (error) {
    console.warn('[Analytics] Failed to identify signed-in PostHog user:', error);
  }
};

export const resetToAnonymousUser = () => {
  const anonymousUserId = getUserId();
  clearTrackedClerkUserId();
  lastIdentifiedPostHogClerkUserId = null;
  lastSyncedPostHogUserProperties = {
    clerkUserId: null,
    properties: {},
  };

  if (gaEnabled) {
    updateGaUserId(anonymousUserId);
  }

  if (!posthogInitialized || !isAnalyticsAllowed()) return;

  updatePostHogAnonymousIdentity();
};

const GAME_SESSION_INACTIVITY_DURATION_MS = 300000;
const GAME_SESSION_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'focusin'];

// Game session tracking hook
export const useGameSession = (gameName, gameDate, gameStartTime, gameOver) => {
  const lastActivityTimeRef = useRef(Date.now());
  const inactivityTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);
  const gameOverRef = useRef(gameOver);
  const sessionRef = useRef({
    gameName,
    gameDate,
    gameStartTime,
  });

  const clearInactivityTimer = useCallback(() => {
    if (!inactivityTimeoutRef.current) return;
    clearTimeout(inactivityTimeoutRef.current);
    inactivityTimeoutRef.current = null;
  }, []);

  // Keep refs in sync so timer callbacks always read fresh values.
  useEffect(() => {
    sessionRef.current = { gameName, gameDate, gameStartTime };
  }, [gameName, gameDate, gameStartTime]);

  useEffect(() => {
    gameOverRef.current = gameOver;
    if (gameOver) {
      clearInactivityTimer();
    }
  }, [gameOver, clearInactivityTimer]);

  // Track user leaving the game (component unmount or beforeunload)
  const trackGameLeave = useCallback(() => {
    const { gameName: currentGameName, gameDate: currentGameDate, gameStartTime: currentGameStartTime } = sessionRef.current;
    if (currentGameStartTime && !gameOverRef.current) {
      const sessionTime = Date.now() - currentGameStartTime;
      logGameEvent('user_left', currentGameName, currentGameDate, {
        session_time: sessionTime,
        last_activity: Date.now() - lastActivityTimeRef.current,
      });
    }
  }, []);

  // Track user inactivity
  const trackInactivity = useCallback(() => {
    const { gameName: currentGameName, gameDate: currentGameDate, gameStartTime: currentGameStartTime } = sessionRef.current;
    if (currentGameStartTime && !gameOverRef.current) {
      const sessionTime = Date.now() - currentGameStartTime;
      logGameEvent('user_inactive', currentGameName, currentGameDate, {
        session_time: sessionTime,
        inactivity_duration: GAME_SESSION_INACTIVITY_DURATION_MS,
      });
    }
    inactivityTimeoutRef.current = null;
  }, []);

  const scheduleInactivityTimer = useCallback(() => {
    if (!isInitializedRef.current || gameOverRef.current) return;
    clearInactivityTimer();
    inactivityTimeoutRef.current = setTimeout(trackInactivity, GAME_SESSION_INACTIVITY_DURATION_MS);
  }, [clearInactivityTimer, trackInactivity]);

  // Update activity timestamp without triggering rerenders.
  const updateActivity = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
    scheduleInactivityTimer();
  }, [scheduleInactivityTimer]);

  // Main effect for setting up session tracking - only runs once per session
  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }

    if (!gameStartTime) {
      return;
    }

    isInitializedRef.current = true;
    lastActivityTimeRef.current = Date.now();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateActivity();
      }
    };

    GAME_SESSION_ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    scheduleInactivityTimer();

    return () => {
      isInitializedRef.current = false;
      clearInactivityTimer();

      GAME_SESSION_ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      trackGameLeave();
    };
  }, [gameStartTime, updateActivity, scheduleInactivityTimer, clearInactivityTimer, trackGameLeave]);

  return {
    updateActivity,
    trackGameLeave,
    trackInactivity,
  };
};

// Utility function to track game start
export const trackGameStart = (gameName, gameDate, additionalData = {}) => {
  const startTime = Date.now();
  logGameEvent('started', gameName, gameDate, {
    start_time: startTime,
    ...additionalData,
  });
  return startTime;
};

// Utility function to track game resume
export const trackGameResume = (gameName, gameDate) => {
  logGameEvent('resumed', gameName, gameDate);
};

// Utility function to track game completion
export const trackGameCompletion = (gameName, gameDate, gameStartTime, gameState, additionalData = {}) => {
  const completionTime = gameStartTime ? Date.now() - gameStartTime : null;
  logGameEvent('completed', gameName, gameDate, {
    completion_time: completionTime,
    ...additionalData,
  });

  return {
    gameName,
    gameDate,
    gameStartTime,
    gameState,
    additionalData,
    completionTime,
  };
};

// Utility function to track game over (failure)
export const trackGameOver = (gameName, gameDate, gameStartTime, gameState, additionalData = {}) => {
  const gameTime = gameStartTime ? Date.now() - gameStartTime : null;
  logGameEvent('over', gameName, gameDate, {
    game_time: gameTime,
    ...additionalData,
  });

  return {
    gameName,
    gameDate,
    gameStartTime,
    gameState,
    additionalData,
    gameTime,
  };
};

// Set user_id
export const setUserId = (userId) => {
  if (!userId) return;

  if (gaEnabled) {
    updateGaUserId(userId);
  }

  if (posthogInitialized && posthogEnabled) {
    try {
      posthog.register(
        sanitizeProperties({
          ...buildCommonProperties(),
          app_user_id: userId,
        })
      );
    } catch (error) {
      console.warn('[Analytics] Failed to set PostHog user ID:', error);
    }
  }

  setUserProperties({ app_user_id: userId });
};

// Set user properties (e.g., language level, user type, etc.)
export const setUserProperties = (properties) => {
  const {
    nextProperties,
    changedProperties,
  } = mergeAnalyticsUserProperties(properties);
  if (Object.keys(changedProperties).length === 0) {
    return;
  }

  const mergedProperties = {
    ...buildCommonProperties(),
    ...nextProperties,
  };

  const gaProps = Object.fromEntries(
    Object.entries(mergedProperties).map(([k, v]) => [k, v === null ? '' : v])
  );

  if (gaEnabled) {
    try {
      ReactGA.gtag('set', 'user_properties', gaProps);
    } catch (error) {
      console.warn('[Analytics] Failed to set GA user properties:', error);
    }
  }

  if (posthogInitialized && posthogEnabled) {
    try {
      posthog.register(sanitizeProperties(changedProperties));
      syncPostHogUserPropertiesIfNeeded(getTrackedClerkUserId(), nextProperties);
    } catch (error) {
      console.warn('[Analytics] Failed to set PostHog user properties:', error);
    }
  }
};

// Track newsletter events
export const logNewsletterEvent = (eventName, eventData) => {
  const namespacedEventName = `newsletter_${eventName}`;
  logEvent(namespacedEventName, eventData);
};

// Track newsletter popup shown
export const trackNewsletterPopupShown = () => {
  logNewsletterEvent('popup_shown', {});
};

// Track newsletter signup attempt
export const trackNewsletterSignupAttempt = (success) => {
  logNewsletterEvent('signup_attempted', {
    success,
  });
};

// Track newsletter popup dismissed
export const trackNewsletterPopupDismissed = () => {
  logNewsletterEvent('popup_dismissed', {});
};

// -----------------------------
// Minimal join analytics helpers
// -----------------------------

const getJoinContext = () => {
  try {
    const { pathname, search } = window.location || { pathname: '', search: '' };
    const params = new URLSearchParams(search || '');
    const modal = params.get('modal') || '';
    const next = params.get('next') || '';
    const wall = params.get('wall') || '';
    return { pathname, modal, next, wall };
  } catch (_) {
    return { pathname: '', modal: '', next: '', wall: '' };
  }
};

export const trackJoinImpression = () => {
  logEvent('join_impression', getJoinContext());
};

export const trackJoinClicked = () => {
  logEvent('join_clicked', getJoinContext());
};

export const trackJoinDismissed = () => {
  logEvent('join_dismissed', getJoinContext());
};
